import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AccessDenied } from '../../../components/access-denied';
import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { fetchAuctionSchedule } from '../api';
import { type AuctionScheduleFilters, type AuctionScheduleStats, type AuctionScheduleView } from '../types';
import { UKRAINE_TIMEZONE, getEffectiveEndDate, isAccessDeniedError } from '../utils';
import { AuctionScheduleCalendarEditor } from './auction-schedule-calendar-editor';
import { AuctionScheduleListEditor } from './auction-schedule-list-editor';
import { AuctionScheduleTimelineEditor } from './auction-schedule-timeline-editor';
import { ViewToggle } from './view-toggle';

/**
 * Auction schedule — `Redesign-D-Admin` / `-Mobile`, screen «Розклад».
 *
 * This is the ORIGINAL per-lot bid-relay board, unchanged in behaviour: it was
 * the sole content of `../auction-schedule-page.tsx` until the admin/auction
 * schedule-parity phase gave it a sibling (the Design D session views in
 * `./session-schedule/`) and hoisted the top-level switch between them to
 * `../../auction-schedule-page.tsx`. Moving this component out of the page
 * file is what "стара per-lot дошка стає окремою вкладкою" means — nothing
 * about the board itself changed, only where it is mounted from.
 *
 * The artboard draws one table of closings: date, who is closing, how many
 * lots, how many client bids, feed status. Our read model is finer-grained —
 * `GET /admin/auction-schedule` starts from the `bids` collection and returns
 * one row per LOT that has client bids, with no auction-house and no feed
 * health anywhere in the projection. So the artboard's six-column geometry is
 * kept and re-pointed at what the API actually serves: the lot takes the
 * "auction" column, its highest bid takes the lot-count column, and the badge
 * column carries the bidding status instead of a feed status. Inventing a
 * house name or a feed colour would have been the easy way to match the
 * picture and the fastest way to mislead an operator.
 *
 * The default view is the list, because that IS the artboard's composition;
 * calendar and timeline stay one tap away and keep every binding they had.
 */
export function LegacyAuctionBoard() {
  const [currentView, setCurrentView] = useState<AuctionScheduleView>('list');
  const [filters, setFilters] = useState<AuctionScheduleFilters>({
    timeframe: 'all',
    status: 'all',
    search: '',
  });
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(
      () => setFilters((prev) => ({ ...prev, search: searchInput })),
      500,
    );
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const scheduleQuery = useQuery({
    queryKey: ['admin-auction-schedule', filters],
    queryFn: () =>
      fetchAuctionSchedule({ filters, page: 1, limit: 100, sortBy: 'end_date', sortOrder: 'asc' }),
  });

  if (isAccessDeniedError(scheduleQuery.error)) {
    return <AccessDenied />;
  }

  const handleRefresh = async () => {
    const result = await scheduleQuery.refetch();
    if (result.isError) {
      console.error('Failed to refresh auction schedule:', result.error);
      toast.error(t('auctionSchedule.messages.error'));
      return;
    }
    toast.success(t('auctionSchedule.messages.refreshed'));
  };

  const setFilter = (key: keyof AuctionScheduleFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const auctions = scheduleQuery.data?.data ?? [];
  const stats = scheduleQuery.data?.stats;
  const isLoading = scheduleQuery.isLoading;
  const nextToday = nextClosingToday(auctions);

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('auctionSchedule.title')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
            <span className="lg:hidden">
              {t('auctionSchedule.board.eyebrowShort', {
                month: currentMonth(),
                count: auctions.length,
              })}
            </span>
            <span className="hidden lg:inline">
              {t('auctionSchedule.board.eyebrow', {
                month: currentMonth(),
                count: auctions.length,
                time: clock(new Date(scheduleQuery.data?.lastUpdated ?? Date.now())),
              })}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleRefresh()}
          disabled={scheduleQuery.isFetching}
          className="hidden h-10 items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink disabled:opacity-50 lg:inline-flex"
        >
          <RefreshCw size={16} strokeWidth={1.75} className={scheduleQuery.isFetching ? 'animate-spin' : ''} />
          {t('auctionSchedule.actions.refresh')}
        </button>
      </div>

      {/*
        Tiles are a DESKTOP affordance. The mobile artboard goes straight from
        the header into the list of closings — a phone screen that spends its
        first 220px on four counts pushes the thing the operator came for below
        the fold, and the counts are already implied by the list underneath.
      */}
      <div className="hidden lg:grid lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] lg:gap-3">
        <StatTile
          label={t('auctionSchedule.board.tiles.closings')}
          value={stats?.totalAuctions}
          sub={t('auctionSchedule.board.tiles.closingsSub', { active: stats?.activeAuctions ?? 0 })}
          rule="line"
        />
        <StatTile
          label={t('auctionSchedule.board.tiles.today')}
          value={stats?.endingToday}
          sub={
            nextToday
              ? t('auctionSchedule.board.tiles.todaySub', { time: nextToday })
              : t('auctionSchedule.board.tiles.todayNone')
          }
          rule="accent"
        />
        <StatTile
          label={t('auctionSchedule.board.tiles.clientBids')}
          value={stats?.totalBids}
          sub={t('auctionSchedule.board.tiles.clientBidsSub', {
            avg: formatAverage(stats),
          })}
          rule="brand"
        />
        <StatTile
          label={t('auctionSchedule.board.tiles.thisWeek')}
          value={stats?.endingThisWeek}
          sub={t('auctionSchedule.board.tiles.thisWeekSub')}
          rule="info"
        />
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 rounded-cf-lg border border-cf-line bg-cf-surface p-2.5 lg:flex-nowrap lg:justify-between lg:gap-3 lg:px-[18px] lg:py-3">
        <div className="flex flex-1 flex-wrap items-center gap-2 lg:flex-none lg:flex-nowrap">
          <div className="relative">
            <Search
              size={15}
              strokeWidth={1.75}
              className="absolute top-1/2 left-3 -translate-y-1/2 text-cf-ink-subtle"
            />
            <input
              type="text"
              placeholder={t('auctionSchedule.board.search')}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="h-10 w-full min-w-[150px] rounded-cf-md border border-cf-line bg-cf-surface pr-3 pl-9 text-sm placeholder:text-cf-ink-subtle focus:border-cf-ink focus:outline-none lg:w-[240px]"
            />
          </div>
          <select
            value={filters.timeframe || 'all'}
            onChange={(event) => setFilter('timeframe', event.target.value)}
            className={SELECT_CLASS}
          >
            <option value="all">{t('auctionSchedule.filters.all')}</option>
            <option value="today">{t('auctionSchedule.filters.today')}</option>
            <option value="thisWeek">{t('auctionSchedule.filters.thisWeek')}</option>
            <option value="thisMonth">{t('auctionSchedule.filters.thisMonth')}</option>
          </select>
          <select
            value={filters.status || 'all'}
            onChange={(event) => setFilter('status', event.target.value)}
            className={SELECT_CLASS}
          >
            <option value="all">{t('auctionSchedule.filters.all')}</option>
            <option value="active">{t('auctionSchedule.filters.status.active')}</option>
            <option value="ending">{t('auctionSchedule.filters.status.ending')}</option>
            <option value="ended">{t('auctionSchedule.filters.status.ended')}</option>
          </select>
        </div>

        <ViewToggle currentView={currentView} onViewChange={setCurrentView} />
      </div>

      {/* ── The board ───────────────────────────────────────────────────── */}
      {currentView === 'list' && (
        <AuctionScheduleListEditor auctions={auctions} loading={isLoading} />
      )}
      {currentView === 'calendar' && (
        <AuctionScheduleCalendarEditor auctions={auctions} loading={isLoading} />
      )}
      {currentView === 'timeline' && (
        <AuctionScheduleTimelineEditor auctions={auctions} loading={isLoading} />
      )}
    </>
  );
}

const SELECT_CLASS =
  'h-10 min-w-0 flex-1 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm focus:border-cf-ink focus:outline-none lg:flex-none';

const RULE: Record<'line' | 'accent' | 'brand' | 'info', string> = {
  line: 'shadow-[inset_0_3px_0_var(--color-cf-line)]',
  accent: 'shadow-[inset_0_3px_0_var(--color-cf-accent)]',
  brand: 'shadow-[inset_0_3px_0_var(--color-cf-brand)]',
  info: 'shadow-[inset_0_3px_0_var(--color-cf-info)]',
};

function StatTile({
  label,
  value,
  sub,
  rule,
}: {
  label: string;
  value: number | undefined;
  sub: string;
  rule: keyof typeof RULE;
}) {
  return (
    <div
      className={`flex flex-col rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:px-[18px] lg:py-4 ${RULE[rule]}`}
    >
      <p className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase lg:tracking-[0.14em]">
        {label}
      </p>
      <p className="mt-2 font-mono text-[24px] leading-none font-medium lg:mt-2.5 lg:text-[30px]">
        {value === undefined ? '…' : formatCount(value)}
      </p>
      <p className="mt-1 text-[11px] text-cf-ink-subtle lg:mt-1.5 lg:text-xs">{sub}</p>
    </div>
  );
}

/** «14» / «1 812» — the artboard sets every count in Mono with a thin space. */
function formatCount(value: number): string {
  return new Intl.NumberFormat('uk-UA').format(value).replace(/[\u00A0\u202F]/g, ' ');
}

/** «1,4» — average bids per lot, one decimal, or «0» when nothing is listed. */
function formatAverage(stats: AuctionScheduleStats | undefined): string {
  if (!stats) return '0';
  return new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 1 }).format(
    stats.averageBidsPerAuction || 0,
  );
}

/** «вересень» — the month the board is showing, in the UI locale. */
function currentMonth(): string {
  return new Intl.DateTimeFormat('uk-UA', { month: 'long' }).format(new Date());
}

function clock(date: Date): string {
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(date);
}

/**
 * «08:00» — the next closing still ahead today, for the "Сьогодні" tile's
 * second line. Returns null once the last one has passed, so the tile says
 * «закриттів більше немає» rather than printing a time already in the past.
 */
function nextClosingToday(
  auctions: Array<{ end_date: string }>,
): string | null {
  const now = new Date();
  const todayKey = new Intl.DateTimeFormat('uk-UA', { timeZone: UKRAINE_TIMEZONE }).format(now);
  const upcoming = auctions
    .map((auction) => {
      const end = new Date(auction.end_date);
      return Number.isNaN(end.getTime()) ? null : end;
    })
    .filter((end): end is Date => end !== null)
    .filter(
      (end) =>
        end.getTime() > now.getTime() &&
        new Intl.DateTimeFormat('uk-UA', { timeZone: UKRAINE_TIMEZONE }).format(end) === todayKey,
    )
    .sort((a, b) => a.getTime() - b.getTime());

  const next = upcoming[0];
  return next ? clock(getEffectiveEndDate(next)) : null;
}
